import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { CircleMarker, MapContainer, TileLayer, Tooltip } from "react-leaflet";
import {
    Alert,
    Box,
    Card,
    CircularProgress,
    Grid,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
    Typography
} from "@mui/material";
import { BarChart } from "@mui/x-charts/BarChart";
import axiosInstance from "../AxiosInstance";
import {
    HeatmapMetric,
    HeatmapStore,
    markerColor,
    markerRadius,
    plotSainsburysStores,
    summariseItl1Regions
} from "./storeHeatmap";
import "leaflet/dist/leaflet.css";

const unitedKingdomCentre: [number, number] = [54.5, -3.5];

function toLatLng(latitude: number, longitude: number): [number, number] {
    return [latitude, longitude];
}

interface EposStorePerformance {
    storeNumber: string;
    storeName: string;
    salesAmount: number | null;
    salesUnits: number | null;
    latitude: number | null;
    longitude: number | null;
    region: string | null;
}

interface EposStoreHeatmapResponse {
    items: EposStorePerformance[];
    isAvailable: boolean;
}

interface EposStoreHeatmapProps {
    retailer: string;
    fromDate: string | null;
    toDate: string | null;
    grain: "weekly" | "daily";
    productIds?: number[];
    brands?: string[];
    productGroups?: string[];
}

const formatUnits = (value: number) =>
    new Intl.NumberFormat("en-GB", { maximumFractionDigits: 0 }).format(value);

const formatCurrency = (value: number) =>
    new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP", maximumFractionDigits: 0 }).format(value);

export default function EposStoreHeatmap({
    retailer,
    fromDate,
    toDate,
    grain,
    productIds,
    brands,
    productGroups
}: EposStoreHeatmapProps) {
    const [metric, setMetric] = useState<HeatmapMetric>("salesUnits");
    const [stores, setStores] = useState<HeatmapStore[]>([]);
    const [loading, setLoading] = useState(true);
    const [available, setAvailable] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        if (!fromDate || !toDate || (productIds && productIds.length === 0)) {
            setStores([]);
            setAvailable(true);
            setLoading(false);
            setError("");
            return;
        }

        const controller = new AbortController();
        const load = async () => {
            setLoading(true);
            setError("");
            try {
                const response = await axiosInstance.get<EposStoreHeatmapResponse>("/epos/performance/stores/heatmap", {
                    params: {
                        retailer,
                        fromDate,
                        toDate,
                        grain,
                        productIds,
                        brands,
                        productGroups
                    },
                    paramsSerializer: { indexes: null },
                    signal: controller.signal
                });

                if (!response.data.isAvailable) {
                    if (!controller.signal.aborted) {
                        setStores([]);
                        setAvailable(false);
                    }
                    return;
                }

                if (!controller.signal.aborted) {
                    setStores(response.data.items.map(store => ({
                        storeNumber: store.storeNumber,
                        storeName: store.storeName,
                        salesUnits: store.salesUnits,
                        salesAmount: store.salesAmount,
                        latitude: store.latitude,
                        longitude: store.longitude,
                        region: store.region
                    })));
                    setAvailable(true);
                }
            } catch (requestError) {
                if (controller.signal.aborted) {
                    return;
                }

                setStores([]);
                if (axios.isAxiosError(requestError)) {
                    const data = requestError.response?.data as { message?: string; detail?: string } | undefined;
                    setError(data?.message ?? data?.detail ?? "Store heatmap could not be loaded.");
                } else {
                    setError("Store heatmap could not be loaded.");
                }
            } finally {
                if (!controller.signal.aborted) {
                    setLoading(false);
                }
            }
        };

        void load();
        return () => controller.abort();
    }, [brands, fromDate, grain, productGroups, productIds, retailer, toDate]);

    const plottedStores = useMemo(
        () => plotSainsburysStores(stores, metric),
        [metric, stores]
    );
    const regionSales = useMemo(
        () => summariseItl1Regions(plottedStores.plotted),
        [plottedStores.plotted]
    );
    const maxValue = plottedStores.plotted.reduce((max, store) => Math.max(max, store.value), 0);
    const metricLabel = metric === "salesUnits" ? "sales units" : "sales amount";
    const formatMetric = (value: number) => metric === "salesUnits" ? formatUnits(value) : formatCurrency(value);

    return (
        <Card variant="outlined" sx={{ mt: 2 }}>
            <Stack
                direction={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "stretch", sm: "center" }}
                spacing={2}
                sx={{ p: { xs: 2, sm: 2.5 }, pb: 2, borderBottom: 1, borderColor: "divider" }}
            >
                <Box>
                    <Typography variant="h6">Store Heatmap</Typography>
                    <Typography color="text.secondary" variant="body2">
                        Store locations come from the retailer directory. The map and regional bars both use {metricLabel} for the selected filters.
                    </Typography>
                </Box>
                <ToggleButtonGroup
                    exclusive
                    size="small"
                    color="primary"
                    value={metric}
                    onChange={(_event, nextMetric: HeatmapMetric | null) => {
                        if (nextMetric) {
                            setMetric(nextMetric);
                        }
                    }}
                    aria-label="Heatmap metric"
                >
                    <ToggleButton value="salesUnits">Units</ToggleButton>
                    <ToggleButton value="salesAmount">Sales amount</ToggleButton>
                </ToggleButtonGroup>
            </Stack>

            {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                    <CircularProgress />
                </Box>
            ) : error ? (
                <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>
            ) : !available ? (
                <Box sx={{ p: { xs: 3, sm: 5 }, textAlign: "center" }}>
                    <Typography variant="h6">Store-level data is not available yet</Typography>
                    <Typography color="text.secondary" sx={{ mt: 1 }}>
                        The heatmap will appear after the first {grain} store-level import has completed.
                    </Typography>
                </Box>
            ) : plottedStores.plotted.length === 0 ? (
                <Box sx={{ p: { xs: 3, sm: 5 }, textAlign: "center" }}>
                    <Typography variant="h6">No stores to plot for these filters</Typography>
                    <Typography color="text.secondary" sx={{ mt: 1 }}>
                        Stores need a catalogue location and a positive value for the selected metric.
                    </Typography>
                </Box>
            ) : (
                <Box>
                    {plottedStores.unlocatedCount > 0 && (
                        <Alert severity="info" sx={{ m: 2, mb: 0 }}>
                            {plottedStores.unlocatedCount === 1
                                ? "1 store with sales has no catalogue location, so it is not on the map."
                                : `${plottedStores.unlocatedCount} stores with sales have no catalogue location, so they are not on the map.`}
                        </Alert>
                    )}
                    <Grid container>
                        <Grid item xs={12} lg={6}>
                            <Box sx={{ height: 520, m: 2, borderRadius: 1, overflow: "hidden" }}>
                                <MapContainer
                                    center={unitedKingdomCentre}
                                    zoom={6}
                                    style={{ height: "100%", width: "100%" }}
                                    scrollWheelZoom
                                >
                                    <TileLayer
                                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                    />
                                    {plottedStores.plotted.map(store => (
                                        <CircleMarker
                                            key={store.storeNumber}
                                            center={toLatLng(store.latitude, store.longitude)}
                                            radius={markerRadius(store.value, maxValue)}
                                            pathOptions={{
                                                color: markerColor(store.value, maxValue),
                                                fillColor: markerColor(store.value, maxValue),
                                                fillOpacity: 0.75,
                                                weight: 1
                                            }}
                                        >
                                            <Tooltip>
                                                <span>
                                                    {store.storeName} ({store.storeNumber})
                                                    <br />
                                                    {formatMetric(store.value)}
                                                    {" "}
                                                    {metricLabel}
                                                </span>
                                            </Tooltip>
                                        </CircleMarker>
                                    ))}
                                </MapContainer>
                            </Box>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ px: 2, pb: 2 }}>
                                <Typography variant="caption" color="text.secondary">Lower</Typography>
                                <Box
                                    sx={{
                                        width: 140,
                                        height: 10,
                                        borderRadius: 5,
                                        background: "linear-gradient(90deg, hsl(48, 85%, 58%), hsl(0, 85%, 40%))"
                                    }}
                                />
                                <Typography variant="caption" color="text.secondary">Higher {metricLabel}</Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ ml: "auto" }}>
                                    {formatUnits(plottedStores.plotted.length)} stores
                                </Typography>
                            </Stack>
                        </Grid>
                        <Grid item xs={12} lg={6} sx={{ pr: { lg: 2 }, pb: 2 }}>
                            <Typography variant="subtitle1" sx={{ mt: 2 }}>Sales by region</Typography>
                            <Typography color="text.secondary" variant="body2" sx={{ mb: 1 }}>
                                ITL1 regions, highest at the top. Totals, not sales per store.
                            </Typography>
                            <BarChart
                                layout="horizontal"
                                height={460}
                                margin={{ top: 8, right: 56, bottom: 32, left: 168 }}
                                yAxis={[{
                                    scaleType: "band",
                                    data: regionSales.regions.map(region => region.region)
                                }]}
                                xAxis={[{
                                    valueFormatter: (value: number) => formatMetric(value),
                                    tickNumber: 4
                                }]}
                                series={[{
                                    data: regionSales.regions.map(region => region.total),
                                    label: metricLabel,
                                    valueFormatter: (value) => formatMetric(value ?? 0)
                                }]}
                                slotProps={{ legend: { hidden: true } }}
                            />
                            {regionSales.unassignedCount > 0 && (
                                <Typography color="text.secondary" variant="caption">
                                    {regionSales.unassignedCount === 1
                                        ? "1 plotted store has no region."
                                        : `${regionSales.unassignedCount} plotted stores have no region.`}
                                </Typography>
                            )}
                        </Grid>
                    </Grid>
                </Box>
            )}
        </Card>
    );
}
